import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import theme from '../../theme';
import {
  isBucketDown,
  bucketStartSeconds,
  bucketGapSeconds,
  historyEndSeconds,
  formatEdge,
  formatGap,
} from '../../libs/heartbeatHistory';

// Shared by the canvas and FleetStatus's .fs-spark-* rules. Slim on purpose, so
// the strip reads as a row of dots rather than bars.
export const STRIP_HEIGHT = 12;

const BAR_GAP = 1; // px between bars when there's room for it
const UP_COLOR = theme.colors.primary;
const DOWN_COLOR = theme.colors.danger;
const NEUTRAL_COLOR = theme.colors.borderLight;

function roundedBar(ctx, x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

// One bar per bucket. Cell count and down threshold both come from the backend,
// so a red cell always agrees with the row's Status pill.
function paintStrip(ctx, buckets, width) {
  ctx.clearRect(0, 0, width, STRIP_HEIGHT);
  if (!buckets || buckets.length === 0) {
    // No card / no sparkline yet: one flat neutral bar, not fake empty cells.
    ctx.fillStyle = NEUTRAL_COLOR;
    roundedBar(ctx, 0, 0, width, STRIP_HEIGHT, 2);
    ctx.fill();
    return;
  }
  const slot = width / buckets.length;
  const barW = Math.max(1, slot - BAR_GAP);
  const radius = Math.min(barW / 2, 2);
  for (let i = 0; i < buckets.length; i += 1) {
    ctx.fillStyle = isBucketDown(buckets[i]) ? DOWN_COLOR : UP_COLOR;
    roundedBar(ctx, i * slot, 0, barW, STRIP_HEIGHT, radius);
    ctx.fill();
  }
}

// Whole-window rollup for the hover title — precomputed on the card, so it
// answers "how bad was the day?" without scrubbing 288 cells.
function stripSummary(history) {
  if (!history?.buckets?.length) return 'Heartbeat history — no data';
  // The real window, not "24h ago ... now" — the right edge is compute time, and
  // this title is the only text a screen reader gets for the canvas.
  const parts = [
    `${formatEdge(history.startTs)} → ${formatEdge(
      historyEndSeconds(history),
    )}`,
    `${Math.round(history.bucketSeconds / 60)}-min buckets`,
  ];
  if (Number.isFinite(history.coverage)) {
    parts.push(`${Math.round(history.coverage * 100)}% coverage`);
  }
  if (history.longestGapSeconds > 0) {
    parts.push(`worst gap ${formatGap(history.longestGapSeconds)}`);
  }
  if (history.longGapCount > 0) {
    // "dropout" is the UI word for the contract's "stall" — must match the
    // Dropouts column, or one number reads as two metrics.
    parts.push(
      `${history.longGapCount} dropout${history.longGapCount === 1 ? '' : 's'}`,
    );
  }
  return parts.join(' · ');
}

export default function HeartbeatSparkline({ history }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [painted, setPainted] = useState(false);
  const [width, setWidth] = useState(0);
  const [tooltip, setTooltip] = useState(null); // { left, text, down }

  const hasHistory = !!history?.buckets?.length;

  // Nothing to fetch — history arrives in props — but painting ~250 canvases up
  // front is waste, so gate on visibility. Once painted, stay painted.
  useEffect(() => {
    if (painted) return undefined;
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setPainted(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setPainted(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [painted]);

  // Track available width — the strip is a responsive, full-width sub-row.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    if (typeof ResizeObserver === 'undefined') {
      const measure = () => setWidth(el.clientWidth);
      measure();
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Redraw on data, width, or the paint gate flipping (when the canvas mounts).
  // Backing store is scaled by devicePixelRatio so bars stay crisp on retina.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.round(STRIP_HEIGHT * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paintStrip(ctx, history ? history.buckets : null, width);
  }, [history, width, painted]);

  const handleMove = useCallback(
    (e) => {
      if (!history?.buckets?.length || width === 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const count = history.buckets.length;
      let i = Math.floor((x / width) * count);
      i = Math.max(0, Math.min(count - 1, i));
      const down = isBucketDown(history.buckets[i]);
      // A 20m stall and a 6h outage are both red; the duration is what triage needs.
      const label = down
        ? `No heartbeat · ${formatGap(bucketGapSeconds(history, i))} gap`
        : 'Up';
      const clock = new Date(
        bucketStartSeconds(history, i) * 1000,
      ).toLocaleString();
      setTooltip({ left: x, text: `${label} · ${clock}`, down });
    },
    [history, width],
  );

  const clearTooltip = useCallback(() => setTooltip(null), []);

  return (
    <div
      className="fs-spark"
      ref={wrapRef}
      onMouseMove={handleMove}
      onMouseLeave={clearTooltip}
      title={stripSummary(history)}
    >
      {painted ? (
        <canvas ref={canvasRef} className="fs-spark-canvas" />
      ) : (
        <div className="fs-spark-idle" />
      )}
      {tooltip && (
        <div className="fs-spark-tip" style={{ left: tooltip.left }}>
          <span
            className="fs-spark-tip-dot"
            style={{ background: tooltip.down ? DOWN_COLOR : UP_COLOR }}
          />
          {tooltip.text}
        </div>
      )}
      {/* aria-hidden: the wrapper's `title` already states the window. */}
      <div className="fs-spark-scale" aria-hidden="true">
        <span>{hasHistory ? formatEdge(history.startTs) : '—'}</span>
        <span>{hasHistory ? formatEdge(historyEndSeconds(history)) : '—'}</span>
      </div>
    </div>
  );
}

HeartbeatSparkline.propTypes = {
  // null when the fridge has no card or no sparkline yet — renders neutral.
  history: PropTypes.shape({
    startTs: PropTypes.number,
    bucketSeconds: PropTypes.number,
    expectedCadenceSeconds: PropTypes.number,
    buckets: PropTypes.arrayOf(PropTypes.number),
    coverage: PropTypes.number,
    longestGapSeconds: PropTypes.number,
    longGapCount: PropTypes.number,
  }),
};

HeartbeatSparkline.defaultProps = {
  history: null,
};
